import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import { Card } from '@/components/ui/card';
import { TableTitle } from '@/components/ui/table-title';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import {
  isDevAdmin,
  useAdminSession,
} from '@/features/internal-admin/lib/admin-session';
import { formatUtils, useTimeAgo } from '@/lib/utils';
import { AdminAccountsOverview } from '@activepieces/shared';

import { HealthStatusLine } from './health-status-line';
import { KpiStrip } from './kpi-strip';
import { MonthlyCharts } from './monthly-charts';

// The indicators table only covers tenants linked to a Corp customer with a
// known tier: saying so keeps these numbers from being read as platform totals.
const CoverageNote = ({
  coverage,
}: {
  coverage: AdminAccountsOverview['coverage'];
}) => {
  const computedAgo = useTimeAgo(new Date(coverage.lastComputedAt ?? 0));

  return (
    <p className="text-xs text-muted-foreground">
      {coverage.lastComputedAt
        ? t(
            'Computed {timeAgo} over {accounts} accounts linked to Corp · full analysis in Metabase, folder Statistiques Produits / Product / Khub',
            {
              timeAgo: computedAgo,
              accounts: formatUtils.formatNumber(coverage.tenantsInTable),
            },
          )
        : t('The nightly indicators job has never run.')}
    </p>
  );
};

// The landing page: one line on whether anything is wrong, a handful of numbers
// and their month-over-month history. Slicing and cohorts stay in Metabase — no
// filters, no date range.
const AdminHomePage = () => {
  const { data: session } = useAdminSession();
  const { data: accounts, isError } = useQuery<AdminAccountsOverview, Error>({
    // No polling: the underlying indicators table moves at most once a day.
    queryKey: ['admin-dashboard-accounts'],
    queryFn: () => adminApi.getDashboardAccounts(),
  });

  return (
    <div className="w-full flex flex-col gap-4">
      <TableTitle
        description={t('Platform status, accounts and usage at a glance.')}
      >
        {t('Overview')}
      </TableTitle>

      {/* It points to Health, which only developers see. */}
      {isDevAdmin(session) && <HealthStatusLine />}

      {isError ? (
        <Card className="p-4">
          <p className="text-sm text-destructive">
            {t('Failed to load the adoption overview')}
          </p>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <KpiStrip accounts={accounts} />
            {accounts && <CoverageNote coverage={accounts.coverage} />}
          </div>
          {accounts && <MonthlyCharts series={accounts.series} />}
        </>
      )}
    </div>
  );
};

export default AdminHomePage;
